import { z } from 'zod'

/**
 * All environment access goes through this module. The schema fails fast at
 * startup with a readable message instead of producing undefined behavior
 * deep inside the app.
 */
const envSchema = z.object({
  VITE_APP_ENV: z.enum(['dev', 'qa', 'prod']).default('dev'),
  VITE_API_BASE_URL: z.url().default('https://jsonplaceholder.typicode.com'),
  VITE_API_TIMEOUT_MS: z.coerce.number().int().positive().default(15_000),
  /** Custom URL scheme of the matching app flavor (eleva / eleva-qa / eleva-dev)
   * — the open-in-app pages build their deep links with it so a QA page opens
   * the QA app. */
  VITE_APP_SCHEME: z.string().default('eleva'),
  /** Google AdSense publisher id (ca-pub-…). Feed ads render only when set. */
  VITE_ADSENSE_CLIENT: z.string().default(''),
  /** Optional AdSense ad-unit slot id for the feed placement. */
  VITE_ADSENSE_SLOT: z.string().default(''),
  /**
   * Google Identity Services web client id (…apps.googleusercontent.com). The
   * Google button renders only when set. The backend verifies the id token
   * against Google:OAuth:ClientIds, so the same id must be listed there.
   */
  VITE_GOOGLE_CLIENT_ID: z.string().default(''),
  /**
   * Sign in with Apple Services ID - the website's counterpart of the app's
   * bundle id (Apple:OAuth:WebClientId on the backend). The Apple button
   * renders only when set, and only on Apple platforms.
   */
  VITE_APPLE_CLIENT_ID: z.string().default(''),
})

const parsed = envSchema.safeParse(import.meta.env)

if (!parsed.success) {
  console.error('Invalid environment variables:', z.treeifyError(parsed.error))
  throw new Error('Invalid environment variables - compare your .env file with .env.example')
}

export const env = {
  appEnv: parsed.data.VITE_APP_ENV,
  appScheme: parsed.data.VITE_APP_SCHEME,
  apiBaseUrl: parsed.data.VITE_API_BASE_URL,
  apiTimeoutMs: parsed.data.VITE_API_TIMEOUT_MS,
  adsenseClient: parsed.data.VITE_ADSENSE_CLIENT,
  adsenseSlot: parsed.data.VITE_ADSENSE_SLOT,
  googleClientId: parsed.data.VITE_GOOGLE_CLIENT_ID,
  appleClientId: parsed.data.VITE_APPLE_CLIENT_ID,
} as const
